import {
  BoardRegistryReadback,
  WORLD_SESSION_TIMES_MAX,
  WorldRoomEntry,
  WorldRoomIndexPage,
  WorldSessionTimes,
  WorldSessionChildrenPage,
  type WorldRoomProjection,
  canonicalWorkroomKey,
} from "@cleo/contracts";
import { completeLegacyBoardProfiles } from "@cleo/shared/boardRegistryWire";
import type { ConvexClient } from "convex/browser";
import { makeFunctionReference } from "convex/server";
import { ConvexError } from "convex/values";
import * as Option from "effect/Option";
import * as Schema from "effect/Schema";

import { NEEDS_THE_MAC } from "../webHost";
import {
  CATALOG_PAGE,
  foldCatalogPages,
  readFollowingPages,
  type WebHostCanvasEntry,
} from "./webHostCanvasCatalog";
import { readWebHostSession } from "./webHostSession";
import { sessionTidyDoor } from "./webHostSessionTidy";
import { readWorldConvexClient } from "./worldConvexClient";
import { attestCanvasGesture, prepareCanvasPersonWrite } from "@cleo/shared/canvasPersonWrite";
import { worldGateDecidedRow, worldGateList } from "@cleo/shared/worldGateRows";
import { pressCanvasSwitch } from "@cleo/shared/canvasDoorPress";
import {
  callWorldMcp,
  WINDOW_CANVAS_READS,
  WINDOW_CANVAS_WRITES,
  WINDOW_PUBLISH_WRITES,
  WINDOW_RELEASE_WRITES,
  WEBSITE_RELEASE_TIMEOUT_MS,
  type WindowCanvasRead,
  type WindowCanvasWrite,
  type WindowTool,
} from "./worldMcp";

/**
 * cleo-web (webHost.ts): the Desk routes the browser host answers itself, from the World.
 *
 * Every World screen of apps/web reads through one Desk route (`fetchPrimary`), which the Desk's Mac server answers by
 * calling a World door under the person's deposited grant (apps/server/src/neucleos/worldDoorHttp.ts). The browser host
 * has no Mac server; it has the window's own live client to the World, signed in as the person (worldConvexClient.ts).
 * This is the seam: for each route below, the browser calls the World's own person door (the one Pocket calls, as the
 * same person) and answers the screen in the exact shape the Desk's server answers, so the screens, their clients and
 * their validators are the same code on both hosts. A route or a request shape with no person door answers null, and
 * `fetchPrimary` says it needs the Mac.
 *
 * The doors, each named with the Desk server's route, the World door the server calls, and the person door used here:
 *
 *   /api/neucleos/world/rooms   page   neucleos_read_world_rooms {page}   → boards:readRooms {cursor}      (the room index)
 *   /api/neucleos/world/rooms   entry  neucleos_read_world_rooms {entry}  → boards:readRooms {entry}       (one room and session)
 *                                                                            after boards:readSessionChildren names the
 *                                                                            room when the screen names only the session
 *   /api/neucleos/world/boards         neucleos_read_boards               → boards:readBoards              (the board registry)
 *   /api/neucleos/world/view-prefs     neucleos_read_world_view_prefs     → prefs:getViewPrefs             (the collapsed rails)
 *                                                                          + prefs:readSessionDecks         (the member's session
 *                                                                            decks: every one, or the one session asked for)
 *   /api/neucleos/world/view-prefs/session-deck
 *                                      neucleos_save_world_session_deck   → prefs:saveSessionDeck           (one session's deck,
 *                                                                            confirmed by prefs:readSessionDecks as the Desk's
 *                                                                            server confirms its save by a readback)
 *   /api/neucleos/canvas/read          neucleos_canvas {action: "read"}   → canvas:read                    (one Canvas revision)
 *   /api/neucleos/canvas/asset-url     neucleos_canvas asset read         → canvasAssets:readUrl           (one stored file's URL)
 *   /api/neucleos/room-universe  catalog  the Mac's catalog walk           → canvas:catalog                 (the Context rail's
 *                                                                            Canvases: the World's Canvases this person may read,
 *                                                                            as the room's read-only catalog; the Mac-only tiers
 *                                                                            answer their typed absence)
 *   /api/neucleos/world/rooms   sessionTimes  neucleos_read_world_rooms {sessionTimes} → boards:readSessionTimes (Can 80: when each
 *                                                                            session was last active and whether the World retired
 *                                                                            it; W-9a3e7442: how a session archived on the Desk
 *                                                                            leaves this rail without a reload)
 *   /api/neucleos/world/sessions/retire  neucleos_retire_session (the Desk's server, worldSessionRetirementHttp.ts)
 *                                                                          → worldSessionRetirementRail:retireOwnSession
 *                                                                            (W-9a3e7442: Archive in this rail retires the person's
 *                                                                            own session in the World, so the Desk drops it too)
 *   /api/neucleos/world/sessions/tidy    the Desk's one tidy (sessionTidyHttp.ts)  → the same tidy (@cleo/shared/sessionTidy) over
 *                                        modes preview and archive                   boards:readSessionChildren (Pocket), boards:readRooms
 *                                                                                    (the directory, each entry), boards:readSessionTimes
 *                                                                                    and retireOwnSession (webHostSessionTidy.ts; W-4568777b:
 *                                                                                    the rail's Tidy up archives here as on the Desk)
 *
 * Still the Mac's: `latestRoot` room reads, the whole-index read, the seen cursor and completion reads, the
 * collapsed-rails save, the release of a pending retirement, the tidy's `changes` and `pins` modes (what this Desk last
 * archived, and the pins Cleo's Desk tidy keeps), every other write. The session decks were the Mac's until
 * 19 Oct 2026 (the World held them for the grant door only); the World's person door (prefs.ts, PR #2275) now answers
 * them to the same member, so a Canvas tab opened on a session's deck in the browser is there again after a reload, and
 * the Desk sees it too. Session times were the Mac's until the same day (W-9a3e7442, boards:readSessionTimes).
 */

export const WEB_HOST_WORLD_DOORS = [
  "/api/neucleos/world/rooms",
  "/api/neucleos/world/boards",
  "/api/neucleos/world/view-prefs",
  "/api/neucleos/world/view-prefs/session-deck",
  "/api/neucleos/canvas/read",
  "/api/neucleos/canvas/asset-url",
  "/api/neucleos/room-universe",
  "/api/neucleos/world/sessions/retire",
  "/api/neucleos/world/sessions/tidy",
  // Step 7.4.1: the Canvas door's reads, through the World's one /mcp door with this window's own grant (worldMcp.ts):
  // a Canvas's shares, publications, friend links, offers, datasets, groups and action history, as the Desk shows them.
  ...WINDOW_CANVAS_READS.map((action) => `/api/neucleos/canvas/${action}` as const),
  // Step 7.4.2: the share sheet's writes, each the person's own press (canvasMcpWriteDoor).
  ...WINDOW_CANVAS_WRITES.map((action) => `/api/neucleos/canvas/${action}` as const),
  // Step 7.4.4a: the Inbox, the person's open cards in this World and their decision on one (gateInstanceMcpDoors).
  "/api/neucleos/world/gate-instances",
  "/api/neucleos/world/gate-instances/decide",
  // Step 7.4.4a-2a: the Inbox's and the bell's read marks (notificationReadDoor).
  "/api/neucleos/notifications/read",
  // Step 7.4.4b-1b: a Canvas Component's actions (actionPrepareDoor, actionDoorDoor).
  "/api/neucleos/canvas/action-prepare",
  "/api/neucleos/canvas/action-door",
] as const;

type DoorPath = (typeof WEB_HOST_WORLD_DOORS)[number];

/** The Desk server's refusal shape and statuses (worldDoorHttp.ts REFUSAL_STATUS), for the kinds the browser can meet. */
const REFUSAL_STATUS = {
  bad_request: 400,
  not_signed_in: 401,
  upstream_refusal: 409,
  transport: 502,
} as const;
type RefusalKind = keyof typeof REFUSAL_STATUS;

type KindRefusal = {
  readonly status: number;
  readonly body: { ok: false; kind: RefusalKind; reason: string };
};
type DoorAnswer =
  | { readonly status: 200; readonly body: Record<string, unknown> }
  | KindRefusal
  /** The Desk's retirement route answers a refusal by its reason word and the World's own words (worldSessionRetirementClient.ts). */
  | { readonly status: number; readonly body: { ok: false; reason: string; detail?: string } }
  /** The Desk's tidy route answers its plan, its run, or a refusal by its reason (sessionTidyHttp.ts). */
  | { readonly status: number; readonly body: Record<string, unknown> };

const refuse = (kind: RefusalKind, reason: string): KindRefusal => ({
  status: REFUSAL_STATUS[kind],
  body: { ok: false, kind, reason },
});
const ok = (body: Record<string, unknown>): DoorAnswer => ({
  status: 200,
  body: { ok: true, ...body },
});

/** What the World said, as a Desk refusal: its own refusal is upstream, a lost connection is transport. */
export function refusalOfWorldError(error: unknown): KindRefusal {
  if (error instanceof ConvexError) {
    const data: unknown = error.data;
    const words =
      typeof data === "string"
        ? data
        : typeof data === "object" &&
            data !== null &&
            typeof (data as { message?: unknown }).message === "string"
          ? (data as { message: string }).message
          : error.message;
    return refuse("upstream_refusal", words.replace(/^\[CONVEX[^\]]*\]\s*/u, "").slice(0, 400));
  }
  const message = (error instanceof Error ? error.message : String(error)).replace(
    /^\[CONVEX[^\]]*\]\s*/u,
    "",
  );
  if (/not signed in|unauthenticated|no identity/iu.test(message))
    return refuse("not_signed_in", "This window is not signed in to the World.");
  return refuse("transport", message.slice(0, 400) || "The World did not answer.");
}

const decodeIndexPage = Schema.decodeUnknownOption(WorldRoomIndexPage);
const decodeRoomEntry = Schema.decodeUnknownOption(WorldRoomEntry);
const decodeSessionTimes = Schema.decodeUnknownOption(WorldSessionTimes);
const decodeBoardRegistry = Schema.decodeUnknownOption(BoardRegistryReadback);

const ROOM_ID = /^R_[A-Za-z0-9._:-]{1,253}$/u;
const SESSION_ID = /^S_[A-Za-z0-9._:-]{1,253}$/u;
const WORKROOM_WIRE = "workroom-v1" as const;

// The World's own person doors (backend/convex/boards.js, prefs.ts, canvas.js), as Pocket calls them.
const readRoomsRef = makeFunctionReference<
  "query",
  {
    worldKey: string;
    cursor?: string;
    entry?: { roomId: string; sessionId: string };
    roomVocabulary: typeof WORKROOM_WIRE;
  },
  unknown
>("boards:readRooms");
const readSessionChildrenRef = makeFunctionReference<
  "query",
  {
    worldKey: string;
    parent: { sessionId: string } | { workroomKey: string };
    roomVocabulary: typeof WORKROOM_WIRE;
  },
  unknown
>("boards:readSessionChildren");
const readBoardsRef = makeFunctionReference<
  "query",
  { worldKey: string; roomVocabulary: typeof WORKROOM_WIRE },
  unknown
>("boards:readBoards");
const getViewPrefsRef = makeFunctionReference<"query", { worldKey: string }, unknown>(
  "prefs:getViewPrefs",
);
const readSessionTimesRef = makeFunctionReference<
  "query",
  { worldKey: string; sessionIds: ReadonlyArray<string> },
  unknown
>("boards:readSessionTimes");
const retireOwnSessionRef = makeFunctionReference<
  "action",
  { worldKey: string; roomId: string; sessionId: string; operationId: string },
  unknown
>("worldSessionRetirementRail:retireOwnSession");
/** A session deck's board as the World holds it (worldViewPrefsClient.ts SessionDeckBoardWire, the same shape). */
interface DeckBoardWire {
  readonly id: string;
  readonly kind: string;
  readonly target?: string;
  readonly payload?: string;
  readonly pin?: {
    readonly revisionKey?: string;
    readonly digest?: string;
    readonly openedAt: number;
  };
}
interface DeckWire {
  readonly sessionId: string;
  readonly activeBoardId?: string;
  readonly boards: ReadonlyArray<DeckBoardWire>;
  readonly updatedAt?: number;
}
const readSessionDecksRef = makeFunctionReference<
  "query",
  { worldKey: string; sessionId?: string; roomVocabulary: typeof WORKROOM_WIRE },
  unknown
>("prefs:readSessionDecks");
const saveSessionDeckRef = makeFunctionReference<
  "mutation",
  {
    worldKey: string;
    sessionId: string;
    activeBoardId?: string;
    boards: ReadonlyArray<DeckBoardWire>;
  },
  unknown
>("prefs:saveSessionDeck");
const canvasReadRef = makeFunctionReference<
  "query",
  { worldKey: string; canvasId: string; sequence?: number },
  unknown
>("canvas:read");
const canvasAssetUrlRef = makeFunctionReference<
  "action",
  { worldKey: string; canvasId: string; sequence: number; sha256: string },
  unknown
>("canvasAssets:readUrl");
type CatalogPage = {
  readonly canvases: ReadonlyArray<unknown>;
  readonly nextCursor: string | null;
};
const canvasCatalogRef = makeFunctionReference<
  "query",
  { worldKey: string; cursor?: string; limit?: number },
  CatalogPage
>("canvas:catalog");

const record = (value: unknown): Record<string, unknown> | undefined =>
  typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
const requiredString = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim() ? value : undefined;

/** The directory generation a room-index cursor names (lib/worldRoomIndex.js roomIndexCursor), or undefined. */
export function generationOfCursor(cursor: unknown): number | undefined {
  if (typeof cursor !== "string" || !cursor) return undefined;
  try {
    const parsed: unknown = JSON.parse(cursor);
    const generation = record(parsed)?.["generation"];
    return typeof generation === "number" && Number.isSafeInteger(generation) && generation > 0
      ? generation
      : undefined;
  } catch {
    return undefined;
  }
}

/**
 * The page the person read (boards:readRooms), in the shape the Desk's server answers: the page contract with the
 * person's Actor, no grant, and the generation when the World's cursor names it. Null when it is not a page of this World.
 */
export function personRoomPageAsIndexPage(
  answer: unknown,
  input: {
    readonly worldKey: string;
    readonly actorId: string;
    readonly cursor?: string | undefined;
  },
): WorldRoomIndexPage | null {
  const page = record(answer);
  if (page === undefined || page["worldKey"] !== input.worldKey) return null;
  const generation =
    generationOfCursor(page["continueCursor"]) ??
    generationOfCursor(input.cursor) ??
    (typeof page["generation"] === "number" ? page["generation"] : undefined);
  const {
    schemaVersion: _version,
    actorId: _actor,
    grantId: _grant,
    named: _named,
    ...rest
  } = page;
  const decoded = decodeIndexPage({
    ...rest,
    schemaVersion: "world-room-index-page-v1",
    actorId: input.actorId,
    ...(generation === undefined ? {} : { generation }),
  });
  return Option.isSome(decoded) ? decoded.value : null;
}

async function worldRoomsDoor(
  client: ConvexClient,
  actorId: string,
  body: Record<string, unknown>,
): Promise<DoorAnswer | null> {
  const worldKey = requiredString(body["worldKey"]);
  if (!worldKey) return refuse("bad_request", "World room reads require one World.");
  // A room's latest root and the whole index at once have no person door: the Mac's.
  if (body["sessionTimes"] !== undefined)
    return sessionTimesDoor(client, worldKey, body["sessionTimes"]);
  const page = record(body["page"]);
  const entry = record(body["entry"]);
  if (body["page"] !== undefined) {
    if (page === undefined || (page["cursor"] !== undefined && !requiredString(page["cursor"])))
      return refuse("bad_request", "A room page read names at most one continuation cursor.");
    const cursor = page["cursor"] as string | undefined;
    let answer: unknown;
    try {
      answer = await client.query(readRoomsRef, {
        worldKey,
        ...(cursor === undefined ? {} : { cursor }),
        roomVocabulary: WORKROOM_WIRE,
      });
    } catch (error) {
      return refusalOfWorldError(error);
    }
    const shaped = personRoomPageAsIndexPage(answer, { worldKey, actorId, cursor });
    return shaped === null
      ? refuse("transport", "The World answered an invalid room page.")
      : ok({ page: shaped });
  }
  if (body["entry"] !== undefined) {
    if (entry === undefined) return refuse("bad_request", "A room entry read names one entry.");
    if (entry["latestRoot"] !== undefined) return null;
    const sessionId = requiredString(entry["sessionId"]);
    let roomId = requiredString(entry["roomId"]);
    if (
      !sessionId ||
      !SESSION_ID.test(sessionId) ||
      (roomId !== undefined && !ROOM_ID.test(roomId))
    )
      return refuse(
        "bad_request",
        "A room entry read names an S_ session and, at most, its R_ room.",
      );
    try {
      // The person door reads an entry by its room and session; the session's own first page names its room.
      roomId ??= await roomOfSession(client, worldKey, sessionId);
      if (roomId === undefined)
        return refuse("upstream_refusal", "The World does not hold this session.");
      const entry = await personRoomEntry(client, { worldKey, roomId, sessionId });
      if (entry === null) return refuse("transport", ENTRY_NOT_CONFIRMED);
      return ok({ entry });
    } catch (error) {
      return refusalOfWorldError(error);
    }
  }
  return null;
}

const ENTRY_NOT_CONFIRMED = "The World did not confirm the requested room entry.";

/** One room entry read as the person (boards:readRooms): null unless the World answered exactly that room and session. */
async function personRoomEntry(
  client: ConvexClient,
  input: { readonly worldKey: string; readonly roomId: string; readonly sessionId: string },
): Promise<WorldRoomEntry | null> {
  const answer = await client.query(readRoomsRef, {
    worldKey: input.worldKey,
    entry: { roomId: input.roomId, sessionId: input.sessionId },
    roomVocabulary: WORKROOM_WIRE,
  });
  const decoded = decodeRoomEntry(answer);
  return Option.isSome(decoded) &&
    decoded.value.worldKey === input.worldKey &&
    decoded.value.room.roomId === input.roomId &&
    decoded.value.session.sessionId === input.sessionId
    ? decoded.value
    : null;
}

/**
 * The same read for the Desk's own birth in the World (deskConversationRoute.ts): the window's sign-in reads back the
 * session it just opened with that same sign-in, never the Mac's stored grant, so one credential decides both.
 */
export async function readRoomEntryAsPerson(
  client: ConvexClient,
  input: { readonly worldKey: string; readonly roomId: string; readonly sessionId: string },
): Promise<
  | { readonly ok: true; readonly entry: WorldRoomEntry; readonly created: false }
  | { readonly ok: false; readonly kind: string; readonly reason: string }
> {
  try {
    const entry = await personRoomEntry(client, input);
    return entry === null
      ? { ok: false, kind: "transport", reason: ENTRY_NOT_CONFIRMED }
      : { ok: true, entry, created: false };
  } catch (error) {
    const refusal = refusalOfWorldError(error);
    return { ok: false, kind: refusal.body.kind, reason: refusal.body.reason };
  }
}

/** Resolve only the pressed Workroom, through the existing bounded person read.
 * A missing rendering-cache row is not evidence that the World has no room.
 * Refusals and invalid answers must not turn into a Mac birth.
 */
export async function readWorkroomAsPerson(
  client: ConvexClient,
  input: { readonly worldKey: string; readonly workroomKey: string },
): Promise<
  | { readonly ok: true; readonly room: WorldRoomProjection | null }
  | { readonly ok: false; readonly kind: string; readonly reason: string }
> {
  try {
    const page = Schema.decodeUnknownSync(WorldSessionChildrenPage)(
      await client.query(readSessionChildrenRef, {
        worldKey: input.worldKey,
        parent: { workroomKey: input.workroomKey },
        roomVocabulary: WORKROOM_WIRE,
      }),
    );
    if (
      page.worldKey !== input.worldKey ||
      (page.parent === null
        ? page.parentId !== null
        : page.parentId !== page.parent.roomId ||
          page.parent.kind !== "workroom" ||
          page.parent.workroomKey !== input.workroomKey)
    )
      return {
        ok: false,
        kind: "invalid_room",
        reason: "The requested Workroom did not read back exactly.",
      };
    return { ok: true, room: page.parent };
  } catch (error) {
    const refusal = refusalOfWorldError(error);
    return { ok: false, kind: refusal.body.kind, reason: refusal.body.reason };
  }
}

/** The room a session sits in, from the session's own first page of children (boards:readSessionChildren); undefined when the World does not hold it. */
async function roomOfSession(
  client: ConvexClient,
  worldKey: string,
  sessionId: string,
): Promise<string | undefined> {
  const children = record(
    await client.query(readSessionChildrenRef, {
      worldKey,
      parent: { sessionId },
      roomVocabulary: WORKROOM_WIRE,
    }),
  );
  const parent = record(children?.["parent"]);
  const named = requiredString(parent?.["roomId"]);
  return children?.["worldKey"] === worldKey &&
    parent?.["sessionId"] === sessionId &&
    named &&
    ROOM_ID.test(named)
    ? named
    : undefined;
}

/**
 * Can 80's session times (W-9a3e7442): when each session was last active and whether the World retired it, read
 * through the person's own door (boards:readSessionTimes) and answered as the Desk's server answers the grant door's
 * read (worldDoorHttp.ts: `{ ok, times }`, the WorldSessionTimes contract, every session one that was asked for). The
 * rail asks once a round for the open sessions it shows and drops the ones the World says are retired, so a session
 * archived on the Desk, by hand or by a tidy, leaves this rail without a reload.
 */
async function sessionTimesDoor(
  client: ConvexClient,
  worldKey: string,
  input: unknown,
): Promise<DoorAnswer> {
  const sessionIds = record(input)?.["sessionIds"];
  if (
    !Array.isArray(sessionIds) ||
    sessionIds.length < 1 ||
    sessionIds.length > WORLD_SESSION_TIMES_MAX ||
    new Set(sessionIds).size !== sessionIds.length ||
    sessionIds.some((id) => typeof id !== "string" || !SESSION_ID.test(id))
  )
    return refuse(
      "bad_request",
      `A session-times read names 1..${WORLD_SESSION_TIMES_MAX} distinct S_ sessions.`,
    );
  let answer: unknown;
  try {
    answer = await client.query(readSessionTimesRef, {
      worldKey,
      sessionIds: sessionIds as ReadonlyArray<string>,
    });
  } catch (error) {
    return refusalOfWorldError(error);
  }
  const decoded = decodeSessionTimes(answer);
  const asked = new Set(sessionIds as ReadonlyArray<string>);
  if (
    Option.isNone(decoded) ||
    decoded.value.worldKey !== worldKey ||
    decoded.value.sessions.some((row) => !asked.has(row.sessionId))
  )
    return refuse("transport", "The World answered invalid session times.");
  return ok({ times: decoded.value });
}

/**
 * Archive in this rail (W-9a3e7442): the Desk's retirement route, answered by the person's own door to the World's
 * retirement (worldSessionRetirementRail:retireOwnSession: reserve under the caller's operationId, the World-held final
 * snapshots, session.retired). The answer is the Desk server's (worldSessionRetirementHttp.ts): `{ ok: true }` once the
 * World says retired, else 409 with the reason word and the World's own words as `detail`, which the rail's client
 * shows (worldSessionRetirementClient.ts retirementRefusalMessage; an open hand under it names that hand, and the rail
 * retires hands first as the Desk does). The client then reads the entry back through the rooms door above.
 */
async function sessionRetireDoor(
  client: ConvexClient,
  body: Record<string, unknown>,
): Promise<DoorAnswer> {
  const worldKey = requiredString(body["worldKey"]);
  const sessionId = requiredString(body["sessionId"]);
  const operationId = requiredString(body["operationId"]);
  let roomId = requiredString(body["roomId"]);
  const keys = Object.keys(body);
  if (
    !worldKey ||
    !sessionId ||
    !SESSION_ID.test(sessionId) ||
    !operationId ||
    operationId.length > 128 ||
    (roomId !== undefined && !ROOM_ID.test(roomId)) ||
    keys.some((key) => !["worldKey", "sessionId", "operationId", "roomId"].includes(key))
  )
    return { status: 400, body: { ok: false, reason: "invalid-request" } };
  const refused = (error: unknown): DoorAnswer => {
    const words = refusalOfWorldError(error);
    return {
      status: words.status,
      body: {
        ok: false,
        reason: words.body.kind === "not_signed_in" ? "world-access-refused" : "world-refused",
        detail: words.body.reason,
      },
    };
  };
  try {
    roomId ??= await roomOfSession(client, worldKey, sessionId);
    if (roomId === undefined)
      return {
        status: 409,
        body: {
          ok: false,
          reason: "world-refused",
          detail: "The World does not hold this session.",
        },
      };
    const answer = record(
      await client.action(retireOwnSessionRef, { worldKey, roomId, sessionId, operationId }),
    );
    if (
      answer?.["worldKey"] !== worldKey ||
      answer?.["sessionId"] !== sessionId ||
      typeof answer["retired"] !== "boolean"
    )
      return { status: 502, body: { ok: false, reason: "retirement-unresolved" } };
    if (answer["retired"] !== true)
      return {
        status: 409,
        body: {
          ok: false,
          reason: "world-refused",
          detail: requiredString(answer["why"]) ?? "the World did not retire it",
        },
      };
    return { status: 200, body: { ok: true } };
  } catch (error) {
    return refused(error);
  }
}

async function worldBoardsDoor(
  client: ConvexClient,
  body: Record<string, unknown>,
): Promise<DoorAnswer> {
  const worldKey = requiredString(body["worldKey"]);
  if (!worldKey)
    return refuse("bad_request", "Board registry reads require exactly one worldKey string.");
  let answer: unknown;
  try {
    answer = await client.query(readBoardsRef, { worldKey, roomVocabulary: WORKROOM_WIRE });
  } catch (error) {
    return refusalOfWorldError(error);
  }
  const registry = Option.getOrUndefined(decodeBoardRegistry(completeLegacyBoardProfiles(answer)));
  if (registry === undefined || registry.worldKey !== worldKey)
    return refuse("transport", "The World answered an invalid board registry.");
  return ok({ registry });
}

const DECK_BOARDS_MAX = 64;

/** One deck board as the Desk's server takes it (worldDoorHttp.ts parseSessionDeckBoards): the same bounds, keys canonical. */
function deckBoardOf(value: unknown): DeckBoardWire | undefined {
  const b = record(value);
  if (b === undefined) return undefined;
  const id = b["id"];
  const kind = b["kind"];
  const target = b["target"];
  const payload = b["payload"];
  if (typeof id !== "string" || id.length === 0 || id.length > 256) return undefined;
  if (typeof kind !== "string" || kind.length === 0 || kind.length > 64) return undefined;
  if (
    target !== undefined &&
    (typeof target !== "string" || target.length === 0 || target.length > 256)
  )
    return undefined;
  if (payload !== undefined && (typeof payload !== "string" || payload.length > 4096))
    return undefined;
  let pin: DeckBoardWire["pin"];
  if (b["pin"] !== undefined) {
    const p = record(b["pin"]);
    if (p === undefined) return undefined;
    const openedAt = p["openedAt"];
    if (typeof openedAt !== "number" || !Number.isFinite(openedAt)) return undefined;
    if (p["revisionKey"] !== undefined && typeof p["revisionKey"] !== "string") return undefined;
    if (p["digest"] !== undefined && typeof p["digest"] !== "string") return undefined;
    pin = {
      ...(p["revisionKey"] === undefined ? {} : { revisionKey: p["revisionKey"] as string }),
      ...(p["digest"] === undefined ? {} : { digest: p["digest"] as string }),
      openedAt,
    };
  }
  return {
    id: canonicalWorkroomKey(id),
    kind: canonicalWorkroomKey(kind),
    ...(target === undefined ? {} : { target: target as string }),
    ...(payload === undefined ? {} : { payload: payload as string }),
    ...(pin === undefined ? {} : { pin }),
  };
}

/** One session deck as the Desk's server takes and answers it (worldDoorHttp.ts parseSessionDeck); undefined when malformed. */
export function deckOf(value: unknown): DeckWire | undefined {
  const d = record(value);
  if (d === undefined) return undefined;
  const sessionId = d["sessionId"];
  if (typeof sessionId !== "string" || !SESSION_ID.test(sessionId)) return undefined;
  if (!Array.isArray(d["boards"]) || d["boards"].length > DECK_BOARDS_MAX) return undefined;
  const boards: DeckBoardWire[] = [];
  for (const raw of d["boards"]) {
    const board = deckBoardOf(raw);
    if (board === undefined) return undefined;
    boards.push(board);
  }
  const active = d["activeBoardId"];
  if (
    active !== undefined &&
    (typeof active !== "string" || !boards.some((b) => b.id === canonicalWorkroomKey(active)))
  )
    return undefined;
  return {
    sessionId,
    ...(active === undefined ? {} : { activeBoardId: canonicalWorkroomKey(active as string) }),
    boards,
    ...(typeof d["updatedAt"] === "number" ? { updatedAt: d["updatedAt"] } : {}),
  };
}

/** The member's session decks the World answered (prefs:readSessionDecks), or undefined when the answer is not that. */
function decksOf(answer: unknown, worldKey: string): ReadonlyArray<DeckWire> | undefined {
  const value = record(answer);
  if (
    value === undefined ||
    value["worldKey"] !== worldKey ||
    !Array.isArray(value["sessionDecks"])
  )
    return undefined;
  const decks: DeckWire[] = [];
  for (const raw of value["sessionDecks"]) {
    const deck = deckOf(raw);
    if (deck === undefined) return undefined;
    decks.push(deck);
  }
  return decks;
}

/**
 * The person's own view prefs, as the Desk's server answers them: the collapsed rails (prefs:getViewPrefs) and the
 * member's session decks (prefs:readSessionDecks; with `sessionId`, that one session's deck alone, and the rails are
 * not read, as the grant door answers a session read with no rails). The seen cursor and the completion reads stay the
 * grant door's: the answer names neither, and the Desk's clients read their absence as unsupported, never as empty.
 */
async function viewPrefsDoor(
  client: ConvexClient,
  body: Record<string, unknown>,
): Promise<DoorAnswer> {
  const worldKey = requiredString(body["worldKey"]);
  const sessionId = body["sessionId"];
  if (!worldKey) return refuse("bad_request", "View-pref reads require a worldKey.");
  if (sessionId !== undefined && (typeof sessionId !== "string" || !SESSION_ID.test(sessionId)))
    return refuse("bad_request", "A session's view-pref read names one S_ session.");
  let decks: unknown;
  let rails: unknown = null;
  try {
    [decks, rails] = await Promise.all([
      client.query(readSessionDecksRef, {
        worldKey,
        ...(sessionId === undefined ? {} : { sessionId }),
        roomVocabulary: WORKROOM_WIRE,
      }),
      sessionId === undefined ? client.query(getViewPrefsRef, { worldKey }) : Promise.resolve(null),
    ]);
  } catch (error) {
    return refusalOfWorldError(error);
  }
  const collapsed = record(rails)?.["collapsed"];
  if (
    rails !== null &&
    (!Array.isArray(collapsed) || collapsed.some((item) => typeof item !== "string"))
  )
    return refuse("transport", "The World answered invalid view prefs.");
  const sessionDecks = decksOf(decks, worldKey);
  if (sessionDecks === undefined)
    return refuse("transport", "The World answered invalid session decks.");
  return ok({
    collapsed: rails === null ? [] : (collapsed as ReadonlyArray<string>),
    sessionDecks,
  });
}

/**
 * One session's deck saved through the person door (prefs:saveSessionDeck), confirmed the way the Desk's server
 * confirms its save: the deck is read back (prefs:readSessionDecks) and must hold the same boards, in order, by id and
 * kind, with the same active board; the answer is the deck as the World holds it.
 */
async function sessionDeckSaveDoor(
  client: ConvexClient,
  body: Record<string, unknown>,
): Promise<DoorAnswer> {
  const worldKey = requiredString(body["worldKey"]);
  const deck = deckOf({
    sessionId: body["sessionId"],
    activeBoardId: body["activeBoardId"],
    boards: body["boards"],
  });
  const allowed = new Set(["worldKey", "sessionId", "activeBoardId", "boards"]);
  if (!worldKey || deck === undefined || Object.keys(body).some((key) => !allowed.has(key)))
    return refuse(
      "bad_request",
      "Session-deck saves require a worldKey, a sessionId (S_…), an optional activeBoardId among the boards, and a boards array of {id, kind, target?, payload?, pin?}.",
    );
  let readback: unknown;
  try {
    await client.mutation(saveSessionDeckRef, {
      worldKey,
      sessionId: deck.sessionId,
      ...(deck.activeBoardId === undefined ? {} : { activeBoardId: deck.activeBoardId }),
      boards: deck.boards,
    });
    readback = await client.query(readSessionDecksRef, {
      worldKey,
      sessionId: deck.sessionId,
      roomVocabulary: WORKROOM_WIRE,
    });
  } catch (error) {
    return refusalOfWorldError(error);
  }
  const stored = decksOf(readback, worldKey)?.find((held) => held.sessionId === deck.sessionId);
  if (
    stored === undefined ||
    stored.activeBoardId !== deck.activeBoardId ||
    stored.boards.length !== deck.boards.length ||
    stored.boards.some((b, i) => b.id !== deck.boards[i]?.id || b.kind !== deck.boards[i]?.kind)
  )
    return refuse("transport", "The session-deck readback does not match the written deck.");
  return ok({ deck: stored });
}

/**
 * One Canvas door read through the World's /mcp door (step 7.4.1), answered in the Desk server's shape
 * (worldDoorHttp.ts canvasDoorHandler: `{ok: true, ...value}`, or its refusal kinds).
 */
async function canvasMcpReadDoor(
  client: ConvexClient,
  actorId: string,
  action: WindowCanvasRead,
  body: Record<string, unknown>,
): Promise<DoorAnswer> {
  const worldKey = requiredString(body["worldKey"]);
  if (!worldKey) return refuse("bad_request", "Choose a World first.");
  const { worldKey: _world, action: _action, ...request } = body;
  const outcome = await callWorldMcp({
    client,
    owner: actorId,
    worldKey,
    tool: "neucleos_canvas",
    args: { ...request, action },
  });
  if (!outcome.ok) return refuse(outcome.kind, outcome.reason);
  const value = record(outcome.value);
  if (value === undefined)
    return refuse(
      "upstream_refusal",
      "The World's Canvas door answered without a structured result.",
    );
  return ok(value);
}

/**
 * One share sheet write through the World's /mcp door (step 7.4.2), as the person's own press: the World keeps the exact
 * operation, the step the declaration names for it is opened with the press as its request and decided as the person's
 * act, then the write goes (the same sequence the Desk's server runs: @cleo/shared/canvasPersonWrite). Answered in the
 * Desk server's shape. Only the window's own screens reach this (fetchPrimary); a Canvas frame never does.
 */
async function canvasMcpWriteDoor(
  client: ConvexClient,
  actorId: string,
  action: WindowCanvasWrite,
  body: Record<string, unknown>,
): Promise<DoorAnswer> {
  const worldKey = requiredString(body["worldKey"]);
  if (!worldKey) return refuse("bad_request", "Choose a World first.");
  const { worldKey: _world, action: _action, ...request } = body;
  // A window never names the public viewer: the World holds it (step 7.4.3). Whatever a caller sent is dropped here, so a
  // browser publication can never carry bytes a Desk press would have replaced (Codex review of #2929).
  delete request["hostScript"];
  delete request["hostStyle"];
  const call = (tool: string, args: Record<string, unknown>) =>
    callWorldMcp({ client, owner: actorId, worldKey, tool: tool as WindowTool, args });
  const prepared = await prepareCanvasPersonWrite({ call, action, request, personPress: true });
  const publishes = WINDOW_PUBLISH_WRITES.includes(action);
  // A website release is attested on its exact authored call too, as the Desk does right before it publishes
  // (canvasPublicPublish.ts beforePublish); the approval target leaves the viewer's digests out, so it is the same step.
  if (prepared.ok && publishes) {
    const requestKey = requiredString(request["requestKey"]);
    if (!requestKey) return refuse("bad_request", "A publication names its requestKey.");
    const attested = await attestCanvasGesture({
      call,
      toolInput: { ...request, action },
      requestKey,
    });
    if (!attested.ok)
      return refuse(
        attested.kind in REFUSAL_STATUS ? (attested.kind as RefusalKind) : "upstream_refusal",
        attested.reason,
      );
  }
  if (!prepared.ok)
    return refuse(
      prepared.kind in REFUSAL_STATUS ? (prepared.kind as RefusalKind) : "upstream_refusal",
      prepared.reason,
    );
  const outcome = WINDOW_RELEASE_WRITES.includes(action)
    ? await callWorldMcp({
        client,
        owner: actorId,
        worldKey,
        tool: "neucleos_canvas",
        args: { ...request, action },
        timeoutMs: WEBSITE_RELEASE_TIMEOUT_MS,
      })
    : await call("neucleos_canvas", { ...request, action });
  if (!outcome.ok) return refuse(outcome.kind, outcome.reason);
  const value = record(outcome.value);
  if (value === undefined)
    return refuse(
      "upstream_refusal",
      "The World's Canvas door answered without a structured result.",
    );
  return ok(value);
}

const GATE_LIST_STATES: ReadonlyArray<unknown> = ["ALL", "CREATED", "DECIDED", "SATISFIED"];
const TARGET_DIGEST = /^[0-9a-f]{64}$/u;

/**
 * The Inbox's list (step 7.4.4a, design §12c): the person's cards in this World through the World's /mcp door with the
 * window's grant (neucleos_list_gate_instances), answered in the Desk server's shape (worldDoorHttp.ts
 * listGateInstancesHandler). A World conversation's card carries its exact call (cloudCall) as its request, as on the
 * Desk; what only a Mac knows (its stop record, its own conversations' request words) is not here, by design.
 */
async function gateInstancesDoor(
  client: ConvexClient,
  actorId: string,
  body: Record<string, unknown>,
): Promise<DoorAnswer> {
  const worldKey = requiredString(body["worldKey"]);
  const unexpected = Object.keys(body).find(
    (key) => !["worldKey", "state", "cursor", "limit"].includes(key),
  );
  if (!worldKey || unexpected !== undefined)
    return refuse(
      "bad_request",
      "Gate-instance lists require a worldKey string and an optional state.",
    );
  if (body["state"] !== undefined && !GATE_LIST_STATES.includes(body["state"]))
    return refuse("bad_request", "state must be CREATED, DECIDED, or SATISFIED.");
  const outcome = await callWorldMcp({
    client,
    owner: actorId,
    worldKey,
    tool: "neucleos_list_gate_instances",
    args: Object.fromEntries(
      ["state", "cursor", "limit"]
        .filter((key) => body[key] !== undefined)
        .map((key) => [key, body[key]]),
    ),
  });
  if (!outcome.ok) return refuse(outcome.kind, outcome.reason);
  const listed = worldGateList(outcome.value);
  if (!listed.ok) return refuse("transport", listed.reason);
  return ok({
    state: listed.state,
    nextCursor: listed.nextCursor,
    attentionCount: listed.attentionCount,
    rows: listed.rows.map(({ row, cloud }) => (cloud === null ? row : { ...row, request: cloud })),
  });
}

/**
 * The person's decision on one of their cards (step 7.4.4a), through the World's door with the window's grant
 * (neucleos_decide_gate_instance), bound to the exact targetDigest it was listed with. The window declares no card, so the
 * World refuses an Approve that only a card may give, in its own words; Decline ends any card. Answered in the Desk
 * server's shape (worldDoorHttp.ts decideGateInstanceHandler), with no invitation link (a window never asks for one).
 */
async function gateDecideDoor(
  client: ConvexClient,
  actorId: string,
  body: Record<string, unknown>,
): Promise<DoorAnswer> {
  const worldKey = requiredString(body["worldKey"]);
  const gateInstanceKey = requiredString(body["gateInstanceKey"]);
  const decisionKey = requiredString(body["decisionKey"]);
  const targetDigest = body["targetDigest"];
  const outcome = body["outcome"];
  // Decision 7: his answer to a coding agent's question, passed to the World as given; the World alone checks it.
  const answer = body["answer"];
  if (
    !worldKey ||
    !gateInstanceKey ||
    !decisionKey ||
    typeof targetDigest !== "string" ||
    !TARGET_DIGEST.test(targetDigest) ||
    (outcome !== "approved" && outcome !== "declined") ||
    (answer !== undefined &&
      (typeof answer !== "object" || answer === null || Array.isArray(answer))) ||
    // A Members card's receipt is the Desk's (W-dd5db5e2); a window shows no such card yet (7.4.4a-2).
    Object.keys(body).some(
      (key) =>
        ![
          "worldKey",
          "gateInstanceKey",
          "targetDigest",
          "outcome",
          "decisionKey",
          "answer",
        ].includes(key),
    )
  )
    return refuse(
      "bad_request",
      "Supply worldKey, gateInstanceKey, the instance's targetDigest, outcome approved or declined, and a decisionKey.",
    );
  const decided = await callWorldMcp({
    client,
    owner: actorId,
    worldKey,
    tool: "neucleos_decide_gate_instance",
    args: {
      gateInstanceKey,
      targetDigest,
      outcome,
      decisionKey,
      ...(answer !== undefined ? { answer } : {}),
    },
  });
  if (!decided.ok) return refuse(decided.kind, decided.reason);
  const row = worldGateDecidedRow(decided.value, gateInstanceKey, targetDigest);
  if (row === null)
    return refuse("transport", "The World door answered the decision without its instance.");
  return ok({ row });
}

/** The press of a Component's action, as the frame's host sends it: these fields and nothing else (CanvasActionHost.tsx). */
const PRESS_FIELDS = ["canvasId", "sequence", "nodeId", "name", "input", "requestKey"];
function pressOf(body: Record<string, unknown>): Record<string, unknown> | null {
  if (
    Object.keys(body).some(
      (key) => key !== "worldKey" && key !== "action" && !PRESS_FIELDS.includes(key),
    )
  )
    return null;
  const press = Object.fromEntries(
    PRESS_FIELDS.filter((key) => key in body).map((key) => [key, body[key]]),
  );
  return requiredString(press["canvasId"]) &&
    typeof press["sequence"] === "number" &&
    requiredString(press["nodeId"]) &&
    requiredString(press["name"]) &&
    requiredString(press["requestKey"])
    ? press
    : null;
}

/**
 * A Component's action, asked of the World first (step 7.4.4b-1b): what it is (action-prepare, through the window's grant;
 * the World admits a window's switch by its grant's own kind). An interaction (state.set, a host action) answers here and
 * is applied in this view; a switch answers its door, pressed next through action-door. A collection write (it would be
 * reviewed and committed in the frame) is not sent from a browser window yet, and says so.
 */
async function actionPrepareDoor(
  client: ConvexClient,
  actorId: string,
  body: Record<string, unknown>,
): Promise<DoorAnswer> {
  const worldKey = requiredString(body["worldKey"]);
  if (!worldKey) return refuse("bad_request", "Choose a World first.");
  const press = pressOf(body);
  if (!press)
    return refuse(
      "bad_request",
      "A Canvas action names its Canvas, revision, placement, action, input and request identity.",
    );
  const outcome = await callWorldMcp({
    client,
    owner: actorId,
    worldKey,
    tool: "neucleos_canvas",
    args: { ...press, action: "action-prepare" },
  });
  if (!outcome.ok) return refuse(outcome.kind, outcome.reason);
  const value = record(outcome.value);
  if (value === undefined)
    return refuse(
      "upstream_refusal",
      "The World's Canvas door answered without a structured result.",
    );
  if (value["status"] === "pending")
    return refuse(
      "upstream_refusal",
      "This Canvas action saves to a collection, which opens on the Desk for now. Nothing was written.",
    );
  return ok(value);
}

/**
 * A Component switch's press (step 7.4.4b-1b): the same sequence the Desk's server sends (@cleo/shared/canvasDoorPress),
 * through the window's grant with the press header. Its card waits in the Inbox; the frame presses the same request again
 * once it is decided. A two-step switch is not pressed from a window (the World refuses its prepare first).
 */
async function actionDoorDoor(
  client: ConvexClient,
  actorId: string,
  body: Record<string, unknown>,
): Promise<DoorAnswer> {
  const worldKey = requiredString(body["worldKey"]);
  if (!worldKey) return refuse("bad_request", "Choose a World first.");
  const press = pressOf(body);
  if (!press)
    return refuse(
      "bad_request",
      "A switch names its Canvas, placement, action and request identity.",
    );
  const pressed = await pressCanvasSwitch({
    press,
    call: async (tool, args, options) => {
      if (tool !== "neucleos_canvas")
        return {
          ok: false,
          kind: "upstream_refusal",
          reason:
            "This switch is not pressed from a browser window yet. Nothing was asked or written.",
        };
      return callWorldMcp({
        client,
        owner: actorId,
        worldKey,
        tool,
        args,
        ...(options.canvasAct ? { canvasAct: options.canvasAct } : {}),
      });
    },
  });
  if (!pressed.ok)
    return refuse(
      pressed.kind in REFUSAL_STATUS ? (pressed.kind as RefusalKind) : "upstream_refusal",
      pressed.reason,
    );
  return pressed.status === "waiting"
    ? ok({ status: "waiting", reason: pressed.reason })
    : ok({ status: "applied" });
}

const NOTIFICATION_KINDS: ReadonlyArray<unknown> = ["gate", "reminder", "activity"];
const NOTIFICATION_STATES: ReadonlyArray<unknown> = ["unread", "read", "dismissed"];

/**
 * The person's read marks on their notifications (step 7.4.4a-2a), as the Desk server's route does them
 * (worldDoorHttp.ts notificationReadHandler): saved through neucleos_save_world_view_prefs with the marks alone, then read
 * back through neucleos_read_world_view_prefs and confirmed mark by mark, through the World's /mcp door with the window's
 * grant. A mark for another person (the window's sign-in changed) is refused before anything is sent.
 */
async function notificationReadDoor(
  client: ConvexClient,
  actorId: string,
  body: Record<string, unknown>,
): Promise<DoorAnswer> {
  const worldKey = requiredString(body["worldKey"]);
  const rows = body["notificationReads"];
  if (
    !worldKey ||
    !requiredString(body["actorId"]) ||
    !Array.isArray(rows) ||
    rows.length > 64 ||
    Object.keys(body).some((key) => !["worldKey", "actorId", "notificationReads"].includes(key))
  )
    return refuse("bad_request", "Choose exact notifications to mark.");
  const marks: Array<{ kind: string; id: string; revision: string; state: string }> = [];
  for (const row of rows) {
    const mark = record(row);
    if (
      !mark ||
      !NOTIFICATION_KINDS.includes(mark["kind"]) ||
      !requiredString(mark["id"]) ||
      !requiredString(mark["revision"]) ||
      !NOTIFICATION_STATES.includes(mark["state"]) ||
      Object.keys(mark).some((key) => !["kind", "id", "revision", "state"].includes(key))
    )
      return refuse("bad_request", "Choose exact notifications to mark.");
    marks.push({
      kind: mark["kind"] as string,
      id: mark["id"] as string,
      revision: mark["revision"] as string,
      state: mark["state"] as string,
    });
  }
  if (body["actorId"] !== actorId)
    return refuse("not_signed_in", "The signed-in person changed. Reopen notifications.");
  const saved = await callWorldMcp({
    client,
    owner: actorId,
    worldKey,
    tool: "neucleos_save_world_view_prefs",
    args: { notificationReads: marks },
  });
  if (!saved.ok) return refuse(saved.kind, saved.reason);
  const read = await callWorldMcp({
    client,
    owner: actorId,
    worldKey,
    tool: "neucleos_read_world_view_prefs",
    args: { notificationReads: marks.map(({ kind, id }) => ({ kind, id })) },
  });
  if (!read.ok) return refuse(read.kind, read.reason);
  const value = record(read.value);
  const receipts = Array.isArray(value?.["notificationReads"])
    ? (value["notificationReads"] as unknown[]).map(record)
    : null;
  if (
    value?.["actorId"] !== actorId ||
    receipts === null ||
    marks.some(
      (mark) =>
        !receipts.some(
          (receipt) =>
            receipt?.["kind"] === mark.kind &&
            receipt["id"] === mark.id &&
            receipt["revision"] === mark.revision &&
            receipt["state"] === mark.state,
        ),
    )
  )
    return refuse("transport", "Notification read state was not confirmed. Refresh and try again.");
  return ok({ actorId, notificationReads: receipts });
}

async function canvasReadDoor(
  client: ConvexClient,
  body: Record<string, unknown>,
): Promise<DoorAnswer> {
  const worldKey = requiredString(body["worldKey"]);
  const canvasId = requiredString(body["canvasId"]);
  const sequence = body["sequence"];
  if (
    !worldKey ||
    !canvasId ||
    (sequence !== undefined && !(typeof sequence === "number" && Number.isSafeInteger(sequence)))
  )
    return refuse(
      "bad_request",
      "A Canvas read names one World, one Canvas and at most one revision sequence.",
    );
  let answer: unknown;
  try {
    answer = await client.query(canvasReadRef, {
      worldKey,
      canvasId,
      ...(sequence === undefined ? {} : { sequence }),
    });
  } catch (error) {
    return refusalOfWorldError(error);
  }
  const value = record(answer);
  if (value === undefined)
    return refuse(
      "upstream_refusal",
      "The World's Canvas door answered without a structured result.",
    );
  return ok(value);
}

async function canvasAssetUrlDoor(
  client: ConvexClient,
  body: Record<string, unknown>,
): Promise<DoorAnswer> {
  const worldKey = requiredString(body["worldKey"]);
  const canvasId = requiredString(body["canvasId"]);
  const sha256 = requiredString(body["sha256"]);
  const sequence = body["sequence"];
  if (
    !worldKey ||
    !canvasId ||
    !sha256 ||
    !(typeof sequence === "number" && Number.isSafeInteger(sequence))
  )
    return refuse(
      "bad_request",
      "An asset read names one World, one Canvas, its revision sequence and the file's sha256.",
    );
  let answer: unknown;
  try {
    answer = await client.action(canvasAssetUrlRef, { worldKey, canvasId, sequence, sha256 });
  } catch (error) {
    return refusalOfWorldError(error);
  }
  const value = record(answer);
  const url = requiredString(value?.["url"]);
  const expiresAt = value?.["expiresAt"];
  if (!url || typeof expiresAt !== "number")
    return refuse("transport", "The World's asset door answered without a URL.");
  return ok({ url, expiresAt });
}

/** The Context rail's member for one Canvas of the World (roomUniverse.ts RoomMemberEntry). Pure. */
export function roomMemberOfCanvas(canvas: WebHostCanvasEntry): {
  readonly id: string;
  readonly label: string;
  readonly contentType?: "canvas" | "idea" | "web-page" | "public-invitation";
} {
  const contentType =
    canvas.type === "idea" || canvas.type === "web-page" || canvas.type === "public-invitation"
      ? canvas.type
      : canvas.type === "website"
        ? ("web-page" as const)
        : undefined;
  return { id: canvas.memberId, label: canvas.name, ...(contentType ? { contentType } : {}) };
}

/**
 * The room's read-only catalog, as the Desk's server answers it for (worldKey, roomId) alone (roomUniverse.ts, the
 * catalog shape): the World's Canvases this person may read fill the world-vfs tier from canvas:catalog, and each tier
 * the Mac walks or relays answers the typed absence the rail already knows how to say. Pure given the pages.
 */
export function roomCatalogOfCanvases(
  coordinate: { readonly worldKey: string; readonly roomId: string },
  canvases: ReadonlyArray<WebHostCanvasEntry>,
): Record<string, unknown> {
  const members = canvases.map(roomMemberOfCanvas);
  const groups = [
    {
      group: "root-codebase",
      state: "root-unbound",
      reason: "No code root is bound in the browser.",
    },
    { group: "world-convex", state: "door-refused", reason: NEEDS_THE_MAC },
    {
      group: "world-files",
      state: "root-unbound",
      reason: "No Files root is bound in the browser.",
    },
    { group: "world-vfs", state: "members", members },
    { group: "world-r2", state: "door-refused", reason: NEEDS_THE_MAC },
  ];
  return {
    universe: {
      state: "catalog",
      worldKey: coordinate.worldKey,
      roomId: coordinate.roomId,
      groups,
      memberIds: members.map((member) => member.id),
    },
  };
}

async function roomUniverseDoor(
  client: ConvexClient,
  body: Record<string, unknown>,
): Promise<DoorAnswer | null> {
  // The addressed shape (a session and its agent thread) carries the selection the Mac holds: the Mac's.
  if (!Object.keys(body).every((key) => key === "worldKey" || key === "roomId")) return null;
  const worldKey = requiredString(body["worldKey"]);
  const roomId = requiredString(body["roomId"]);
  if (!worldKey || !roomId)
    return refuse("bad_request", "A room catalog read names one World and one room.");
  try {
    const first = await client.query(canvasCatalogRef, { worldKey, limit: CATALOG_PAGE });
    const { pages } = await readFollowingPages(
      (args) => client.query(canvasCatalogRef, args),
      worldKey,
      first,
    );
    return ok(roomCatalogOfCanvases({ worldKey, roomId }, foldCatalogPages([first, ...pages])));
  } catch (error) {
    return refusalOfWorldError(error);
  }
}

const asResponse = (answer: DoorAnswer): Response =>
  new Response(JSON.stringify(answer.body), {
    status: answer.status,
    headers: { "content-type": "application/json", "cache-control": "no-store" },
  });

/** The request's JSON body, or undefined when it is not one object. */
function bodyOf(init: RequestInit | undefined): Record<string, unknown> | undefined {
  if (typeof init?.body !== "string") return undefined;
  try {
    return record(JSON.parse(init.body));
  } catch {
    return undefined;
  }
}

/**
 * The browser host's answer to a Desk route, read from the World with the window's own sign-in; null when the route or
 * the request shape has no person door (the caller then says it needs the Mac).
 */
export async function answerWebHostWorldDoor(
  path: string,
  init?: RequestInit,
): Promise<Response | null> {
  if (!(WEB_HOST_WORLD_DOORS as ReadonlyArray<string>).includes(path)) return null;
  if ((init?.method ?? "GET").toUpperCase() !== "POST") return null;
  const body = bodyOf(init);
  if (body === undefined)
    return asResponse(refuse("bad_request", "The request body must be one JSON object."));
  const client = readWorldConvexClient();
  const session = readWebHostSession();
  if (client === null || session.state !== "accepted")
    return asResponse(refuse("not_signed_in", "This window is not signed in to the World."));
  const door = path as DoorPath;
  const canvasRead = WINDOW_CANVAS_READS.find(
    (action) => door === `/api/neucleos/canvas/${action}`,
  );
  if (canvasRead !== undefined)
    return asResponse(await canvasMcpReadDoor(client, session.actorId, canvasRead, body));
  const canvasWrite = WINDOW_CANVAS_WRITES.find(
    (action) => door === `/api/neucleos/canvas/${action}`,
  );
  if (canvasWrite !== undefined)
    return asResponse(await canvasMcpWriteDoor(client, session.actorId, canvasWrite, body));
  if (door === "/api/neucleos/canvas/action-prepare")
    return asResponse(await actionPrepareDoor(client, session.actorId, body));
  if (door === "/api/neucleos/canvas/action-door")
    return asResponse(await actionDoorDoor(client, session.actorId, body));
  if (door === "/api/neucleos/notifications/read")
    return asResponse(await notificationReadDoor(client, session.actorId, body));
  if (door === "/api/neucleos/world/gate-instances")
    return asResponse(await gateInstancesDoor(client, session.actorId, body));
  if (door === "/api/neucleos/world/gate-instances/decide")
    return asResponse(await gateDecideDoor(client, session.actorId, body));
  const answer =
    door === "/api/neucleos/world/rooms"
      ? await worldRoomsDoor(client, session.actorId, body)
      : door === "/api/neucleos/world/boards"
        ? await worldBoardsDoor(client, body)
        : door === "/api/neucleos/world/view-prefs"
          ? await viewPrefsDoor(client, body)
          : door === "/api/neucleos/world/view-prefs/session-deck"
            ? await sessionDeckSaveDoor(client, body)
            : door === "/api/neucleos/canvas/read"
              ? await canvasReadDoor(client, body)
              : door === "/api/neucleos/canvas/asset-url"
                ? await canvasAssetUrlDoor(client, body)
                : door === "/api/neucleos/world/sessions/retire"
                  ? await sessionRetireDoor(client, body)
                  : door === "/api/neucleos/world/sessions/tidy"
                    ? await sessionTidyDoor(client, session.actorId, body)
                    : await roomUniverseDoor(client, body);
  return answer === null ? null : asResponse(answer);
}
